import React, {useEffect, useRef, useState} from 'react';
import {Button, Container, Form, FormControl} from 'react-bootstrap';
import {Link} from 'react-router-dom';

import '../styling/footer.css';
import XenkiaAWSconfig from '../config';

import youtube_icon from '../icons/youtube.png';
import linkedin_icon from '../icons/linkedin.png';
import x_icon from '../icons/x.png';
import facebook_icon from '../icons/facebook.png';
import instagram_icon from '../icons/instagram.png';
import f6s_icon from '../icons/f6s-icon.png';

const isValidEmailAddress = value => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());

export default function HomeFooter() {
    const [email, setEmail] = useState('');
    const [notice, setNotice] = useState(null);
    const [noticeVisible, setNoticeVisible] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const noticeTimer = useRef(null);
    const noticeClearTimer = useRef(null);

    const clearNoticeTimers = () => {
        window.clearTimeout(noticeTimer.current);
        window.clearTimeout(noticeClearTimer.current);
        noticeTimer.current = null;
        noticeClearTimer.current = null;
    };

    const hideNotice = () => {
        window.clearTimeout(noticeTimer.current);
        noticeTimer.current = null;
        setNoticeVisible(false);
        noticeClearTimer.current = window.setTimeout(() => {
            setNotice(null);
            noticeClearTimer.current = null;
        }, 350);
    };

    const showNotice = (title, message, type) => {
        clearNoticeTimers();
        setNotice({title, message, type});
        setNoticeVisible(true);

        if (type === 'success') {
            noticeTimer.current = window.setTimeout(hideNotice, 4000);
        }
    };

    useEffect(() => () => clearNoticeTimers(), []);

    const submitNewsletter = async event => {
        event.preventDefault();
        if (!isValidEmailAddress(email) || submitting) return;

        const submittedEmail = email.trim();
        setSubmitting(true);

        try {
            const response = await fetch(XenkiaAWSconfig.newsletterSubscription.apiUrl, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({email: submittedEmail})
            });
            const result = await response.json();
            const body = result && result.body ? result.body : {};
            const status = body.status || 'Subscription update';
            const noticeType = /success|subscribed/i.test(status) ? 'success' : 'error';

            setEmail('');
            showNotice(status, body.message || 'Please try again in a moment.', noticeType);
        } catch (error) {
            showNotice(
                error instanceof TypeError ? 'Connection problem' : "Couldn't subscribe",
                'Please check your connection and try again.',
                'error'
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <footer className="home-footer">
            <Container className="home-footer-container">
                <div className="home-footer-main">
                    <div className="home-footer-brand-panel">
                        <p className="home-footer-brand">XENKIA <span>3D + AR</span></p>
                        <h2 className="home-footer-title">Help customers see the details that matter.</h2>
                        <p className="home-footer-description">
                            Create more engaging product experiences with interactive 3D and augmented reality.
                        </p>
                        <p className="home-footer-contact-email">
                            Questions? <a href="mailto:info@xenkia.com">info@xenkia.com</a>
                        </p>
                        <Link className="home-footer-contact-link" to="/contact">
                            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                <path d="M4 5.75h16v12.5H4z" />
                                <path d="m4.5 7 7.5 6 7.5-6" />
                            </svg>
                            <span>Contact us</span>
                        </Link>
                    </div>

                    <div className="home-footer-connect">
                        <section className="home-footer-newsletter" aria-labelledby="footer-newsletter-title">
                            <p className="footer-newsletter-eyebrow">NEWSLETTER</p>
                            <h3 className="footer-newsletter-title" id="footer-newsletter-title">Stay in the loop</h3>
                            <p className="footer-newsletter-description">Occasional updates on new features and product news.</p>
                            <Form className="footer-newsletter-form" onSubmit={submitNewsletter}>
                                <div className={`footer-newsletter-controls${notice ? ' has-notice' : ''}`}>
                                    {notice && (
                                        <div
                                            className={`footer-subscription-notice footer-subscription-notice--${notice.type} ${noticeVisible ? 'is-visible' : 'is-hiding'}`}
                                            role="status"
                                            aria-live="polite"
                                        >
                                            <span className="footer-subscription-notice-icon" aria-hidden="true">
                                                {notice.type === 'success' ? String.fromCharCode(10003) : '!'}
                                            </span>
                                            <div className="footer-subscription-notice-copy">
                                                <strong>{notice.title}</strong>
                                                <p>{notice.message}</p>
                                            </div>
                                            <button
                                                type="button"
                                                className="footer-subscription-notice-dismiss"
                                                aria-label="Dismiss subscription message"
                                                onClick={hideNotice}
                                            >
                                                &times;
                                            </button>
                                        </div>
                                    )}
                                    <FormControl
                                        type="email"
                                        placeholder="Your email address"
                                        aria-label="Your email address"
                                        className="footer-newsletter-input"
                                        value={email}
                                        onChange={event => {
                                            clearNoticeTimers();
                                            setEmail(event.target.value);
                                            setNotice(null);
                                            setNoticeVisible(false);
                                        }}
                                    />
                                    <Button
                                        type="submit"
                                        className={`footer-newsletter-button${submitting ? ' is-submitting' : ''}`}
                                        variant="info"
                                        disabled={!isValidEmailAddress(email) || submitting}
                                    >
                                        <span className="footer-newsletter-button-content">
                                            {submitting && <span className="footer-submit-spinner" aria-hidden="true" />}
                                            {submitting ? 'Subscribing...' : 'Subscribe'}
                                        </span>
                                    </Button>
                                </div>
                            </Form>
                        </section>

                        <div className="footer-social-block">
                            <p className="footer-social-label">Follow Xenkia</p>
                            <nav className="footer-social-links" aria-label="Social media">
                                <a href="https://www.linkedin.com/company/xenkia" target="_blank" rel="noreferrer" aria-label="Xenkia on LinkedIn">
                                    <img src={linkedin_icon} alt="" />
                                </a>
                                <a href="https://www.facebook.com/Xenkia-100151998649559" target="_blank" rel="noreferrer" aria-label="Xenkia on Facebook">
                                    <img src={facebook_icon} alt="" />
                                </a>
                                <a href="https://x.com/Xenkia1" target="_blank" rel="noreferrer" aria-label="Xenkia on X">
                                    <img src={x_icon} alt="" />
                                </a>
                                <a href="https://www.instagram.com/XenkiaAR/" target="_blank" rel="noreferrer" aria-label="Xenkia on Instagram">
                                    <img src={instagram_icon} alt="" />
                                </a>
                                <a href="https://www.youtube.com/channel/UCW7OvJfZ6iZ2fxo5b2hsfMA" target="_blank" rel="noreferrer" aria-label="Xenkia on YouTube">
                                    <img src={youtube_icon} alt="" />
                                </a>
                                <a href="https://www.f6s.com/xenkia?follow=1" target="_blank" rel="noreferrer" aria-label="Xenkia on F6S">
                                    <img src={f6s_icon} alt="" />
                                </a>
                            </nav>
                        </div>
                    </div>
                </div>

                <div className="home-footer-bottom">
                    <p>&#169; {new Date().getFullYear()} Xenkia&#8482;, Inc. All Rights Reserved.</p>
                </div>
            </Container>
        </footer>
    );
}
